// ── usePositionPnl — Shared P&L calculation hook ────────────────
// Used by both PositionTracker and ValueDashboard's PositionSidebar.
// Derives unrealized P&L from accountStore positions + marketStore prices.

import { useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';

export interface PositionWithPnl {
  ticker: string;
  side: 'yes' | 'no';
  position: number;
  averagePrice: number;
  currentPrice: number | null;
  unrealizedPnl: number | null;
}

export function usePositionPnl() {
  const positions = useAccountStore((s) => s.positions);
  const version = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);

  // Force re-derive when Hydra prices update
  void version;

  const positionsWithPnl: PositionWithPnl[] = positions.map((pos) => {
    // marketStore returns the YES mid; flip to side-native for NO so the
    // value compares directly against averagePrice (which is the actual
    // price paid for the side held — 72c on a NO contract means the user
    // paid 72c for that NO contract, not the YES-equivalent).
    const yesMid = getMarketPrice(pos.ticker);
    const currentPrice =
      yesMid == null ? null : pos.side === 'yes' ? yesMid : 100 - yesMid;
    let unrealizedPnl: number | null = null;
    if (currentPrice !== null) {
      unrealizedPnl = (currentPrice - pos.averagePrice) * pos.position;
    }
    return { ...pos, currentPrice, unrealizedPnl };
  });

  const totalPnl = positionsWithPnl.reduce((sum, p) => sum + (p.unrealizedPnl ?? 0), 0);

  return { positionsWithPnl, totalPnl };
}
